/* eslint-disable no-plusplus */
import axios from 'axios';

import { detectTypePrefix, getLanguagePrefix } from './menu';

import { IconName } from 'components/atoms/Icon';
import { OptionType } from 'components/molecules/Pulldown';
import i18n, { detectLanguage } from 'i18n';
import { IndividualCustomerServiceDataTypes } from 'services/individualCustomers/types';
import { MenuItemDataTypes } from 'services/menus/types';
import { BASE_URL } from 'utils/constants';

function mapModifiers(
  baseClassName: string,
  ...modifiers: (string | string[] | false | undefined)[]
): string {
  return modifiers
    .reduce<string[]>(
      (acc, m) => (!m ? acc : [...acc, ...(typeof m === 'string' ? [m] : m)]),
      [],
    )
    .map((m) => `-${m}`)
    .reduce<string>(
      (classNames, suffix) => `${classNames} ${baseClassName}${suffix}`,
      baseClassName,
    );
}

export default mapModifiers;

/*!
 * Scroll down to next block element
 */
export function scrollDownNextSection(ref: React.RefObject<HTMLDivElement>) {
  if (ref && ref.current) {
    window.scrollTo({ behavior: 'smooth', top: ref.current.offsetTop - 68 }); // Minus header height
  }
}

/*!
 * getMousePosition(event) - cross browser normalizing of:
 * clientX, clientY, screenX, screenY, offsetX, offsetY, pageX, pageY
 * HTMLElement
 */
export function getMousePosition(
  evt:
    | React.MouseEvent<SVGPathElement, MouseEvent>
    | React.MouseEvent<SVGRectElement, MouseEvent>,
  item: HTMLDivElement,
) {
  let { pageX } = evt;
  let { pageY } = evt;
  if (pageX === undefined) {
    pageX = evt.clientX
      + document.body.scrollLeft
      + document.documentElement.scrollLeft;
    pageY = evt.clientY
      + document.body.scrollTop
      + document.documentElement.scrollTop;
  }

  const rect = item.getBoundingClientRect();
  const offsetX = evt.clientX - rect.left;
  const offsetY = evt.clientY - rect.top;

  return {
    client: { x: evt.clientX, y: evt.clientY }, // relative to the viewport
    screen: { x: evt.screenX, y: evt.screenY }, // relative to the physical screen
    offset: { x: offsetX, y: offsetY }, // relative to the event target
    page: { x: pageX, y: pageY }, // relative to the html document
  };
}

export function getDimensions(ele: HTMLDivElement) {
  const { width, height } = ele.getBoundingClientRect();
  const { offsetTop } = ele;
  const offsetBottom = offsetTop + height;

  return {
    width,
    height,
    offsetTop,
    offsetBottom,
  };
}

export function scrollStop(callback: (value: any) => void, time = 2000) {
  // Make sure a valid callback was provided
  if (!callback || typeof callback !== 'function') return;

  // Setup scrolling variable
  let isScrolling: any;

  // Listen for scroll events
  window.addEventListener(
    'scroll',
    () => {
      // Clear our timeout throughout the scroll
      window.clearTimeout(isScrolling);

      // Set a timeout to run after scrolling ends
      isScrolling = setTimeout(callback, time);
    },
    false,
  );
}

export function getMonth(date: Date) {
  return new Date(date).getMonth();
}

export function range(start: number, end: number) {
  return new Array(end - start + 1).fill(undefined).map((_, i) => ({
    label: String(end - i),
    value: String(end - i),
  }));
}

export const years = range(1960, new Date().getFullYear());

export const months = [
  { label: 'January', value: 'January' },
  { label: 'February', value: 'February' },
  { label: 'March', value: 'March' },
  { label: 'April', value: 'April' },
  { label: 'May', value: 'May' },
  { label: 'June', value: 'June' },
  { label: 'July', value: 'July' },
  { label: 'August', value: 'August' },
  { label: 'September', value: 'September' },
  { label: 'October', value: 'October' },
  { label: 'November', value: 'November' },
  { label: 'December', value: 'December' },
];

export const handleScrollCenter = (
  ref: React.RefObject<HTMLDivElement | null>,
  classNameEleActive: string,
) => {
  const eleScroll = ref.current;
  const eleActive = document.querySelector(classNameEleActive);
  if (!eleActive || !eleScroll) return;
  // get width element scroll
  const widthEleScroll = eleScroll.getBoundingClientRect().width;
  // get distance element scroll compared to y window
  const xEleScroll = eleScroll.getBoundingClientRect().x;
  // get width element active
  const widthEleActive = eleActive.getBoundingClientRect().width;
  // get distance element active compared to y window
  const xEleActive = eleActive.getBoundingClientRect().x;
  // get position sroll bar
  const positionScroll = eleScroll.scrollLeft;
  const scrollX = xEleActive
    - xEleScroll
    + widthEleActive / 2
    + positionScroll
    - widthEleScroll / 2;
  eleScroll.scroll({
    left: scrollX,
    behavior: 'smooth',
  });
};

export const downloadFunc = (url: string, filename?: string, target?: string): void => {
  const a = document.createElement('a');
  a.target = target || '_blank';
  a.download = filename || `${Date.now()}`;
  a.href = url;
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
};

export const downloadFileInTab = (url: string, filename?: string): void => {
  const a = document.createElement('a');
  a.target = '_self';
  a.download = filename || `${Date.now()}`;
  a.href = url;
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
  setTimeout(() => {
    window.close();
  }, 500);
};

export const renderColor = (theme: string, increase: boolean) => {
  if (theme === 'light') {
    if (increase) return 'kellyGreen1';
    return 'red';
  }
  if (increase) return 'chartreuse';
  return 'sunsetOrange';
};

export const renderIcon = (theme: string, increase: boolean) => {
  if (theme === 'light') {
    if (increase) return 'arrowUpGreen';
    return 'arrowDownRed';
  }
  if (increase) return 'arrowUpGreenDark';
  return 'arrowDownRedDark';
};

export const getImageURL = (imgUrl?: string) => `${BASE_URL || ''}${imgUrl || ''}`;

export const getBaseURL = (url?: string) => `${window.location.origin}${url || ''}`;

export const dataURLtoFile = (dataUrl: string, filename: string) => {
  const arr = dataUrl.split(',');
  const mine = 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mine });
};

export function convertIndividualServiceList(
  list: IndividualCustomerServiceDataTypes[],
) {
  if (list.length > 0) {
    return list.map((item) => ({
      imgSrc: getImageURL(item.icon),
      title: item.name,
      banner: getImageURL(item.icon),
      alt: item.name,
      slug: '#',
    }));
  }
  return [];
}

export function numberWithPrefix(x?: number, prefix?: string) {
  if (!x) return '-';
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, prefix || ',');
}

export const formatDateDDMMYYYY = (date?: string, isYMDFormat?: boolean) => {
  if (!date) return '';
  const dateFormat = new Date(date);
  let day: string | number = dateFormat.getDate();
  let month: string | number = dateFormat.getMonth() + 1;
  if (day < 10) {
    day = `0${day}`;
  }
  if (month < 10) {
    month = `0${month}`;
  }
  return !isYMDFormat ? `${day}/${month}/${dateFormat.getFullYear()}` : `${dateFormat.getFullYear()}-${month}-${day}`;
};

export const formatDateDashYYYYMMDD = (date?: string) => {
  // YYYY-MM-DD
  if (!date) return '';
  const dateFormat = new Date(date);
  let day: string | number = dateFormat.getDate();
  let month: string | number = dateFormat.getMonth() + 1;
  if (day < 10) {
    day = `0${day}`;
  }
  if (month < 10) {
    month = `0${month}`;
  }
  return `${dateFormat.getFullYear()}-${month}-${day}`;
};

export const formatDateYYYYDDMM = (date?: string | Date) => {
  if (!date) return '';
  const dateFormat = typeof (date) === 'string' ? new Date(date) : date;
  let day: string | number = dateFormat.getDate();
  let month: string | number = dateFormat.getMonth() + 1;
  if (day < 10) {
    day = `0${day}`;
  }
  if (month < 10) {
    month = `0${month}`;
  }
  return `${dateFormat.getFullYear()}-${month}-${day}`;
};

export const ordering = (key: string, order: 'asc' | 'desc') => (
  a: any,
  b: any,
) => {
  // eslint-disable-next-line no-prototype-builtins
  if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
    return 0;
  }

  const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
  const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

  let comparison = 0;
  if (varA > varB) {
    comparison = 1;
  } else if (varA < varB) {
    comparison = -1;
  }
  return order === 'desc' ? comparison * -1 : comparison;
};

export const sortArrayById = (
  arr: any[],
  key: string,
  order: 'asc' | 'desc',
) => {
  if (arr && arr.length === 0) return [];
  return arr?.sort(ordering(key, order));
};

export const calDifferenceNumber = (a: number, b: number) => {
  if (a > b) return a - b;
  return b - a;
};

export function openInNewTab(href?: string) {
  if (!href) return;
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const timingTransaction = (date: string) => {
  const dateFm = new Date(date);
  const month = dateFm.getMonth() + 1 < 10
    ? `0${dateFm.getMonth() + 1}`
    : dateFm.getMonth() + 1;
  const year = dateFm.getFullYear();
  return {
    day:
      dateFm.getDate() < 10
        ? `0${dateFm.getDate()}`
        : dateFm.getDate().toString(),
    monthYear: `${month}/${year.toString()}`,
    month,
  };
};

export function convertLocalToUTCDate(date: Date) {
  if (!date) {
    return date;
  }
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
}

export const calculateChange = (
  priceCloseAdjust: number,
  priceClose: number,
) => {
  const changed = priceCloseAdjust - priceClose;
  return {
    increase: !(changed < 0),
    number: renderValue(changed),
    percent: renderValue((changed / priceClose) * 100),
  };
};

export const convertHangOnMenu = (list: MenuItemDataTypes[]) => list?.map((item) => {
  let link: string;
  if (item.reference?.slug) {
    const prefix = detectTypePrefix(item.type, i18n.language);
    link = `/${getLanguagePrefix(i18n.language)}${prefix}/${item.reference?.slug}`;
  } else if (item.link) {
    link = `${item.link}`;
  } else {
    link = '#';
  }
  return {
    title: item.title,
    iconName: item.icon as IconName,
    iconImage: item.iconImage || '',
    href: link,
    target: item.target,
    type: item.type,
  };
});

export const getDateInfoArrayYYYYMMDD = (date?: Date) => {
  const newDate = date ? new Date(date) : new Date();
  return [newDate.getFullYear(), newDate.getMonth() + 1, newDate.getDate()];
};

export const formatTimeHHMMSS = (date?: string) => {
  const formatDate = date ? new Date(date) : new Date();
  let hours: string | number = formatDate.getHours();
  let minute: string | number = formatDate.getMinutes();
  let second: string | number = formatDate.getSeconds();
  if (hours < 10) {
    hours = `0${hours}`;
  }

  if (minute < 10) {
    minute = `0${minute}`;
  }

  if (second < 10) {
    second = `0${second}`;
  }

  return `${hours}:${minute}:${second}`;
};

export const checkExternalUrl = (str?: string) => {
  if (!str) return false;
  const tareaRegex = /^(http|https|tel)/;
  return tareaRegex.test(String(str).toLowerCase());
};

export const checkIsStorage = (str?: string): boolean => {
  if (!str) return false;
  const indexStr = str.includes('storage/');
  // const tareaRegex = /^(storage)/;
  // return tareaRegex.test(String(str).toLowerCase());
  return indexStr;
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const isSameDateString = (
  dateOne: string | Date,
  dateTwo: string | Date,
) => {
  const parseOne = new Date(dateOne);
  const parseTwo = new Date(dateTwo);

  return (
    parseOne.getFullYear() === parseTwo.getFullYear()
    && parseOne.getDate() === parseTwo.getDate()
    && parseOne.getMonth() === parseTwo.getMonth()
  );
};

export function readObjProps<T, K extends keyof T>(obj: T, key: K) {
  return obj[key];
}

export const returnIndexQuotes = (code: string) => {
  switch (code) {
    case '10':
      return 'HOSE';
    case '02':
      return 'HNX';
    case '04':
      return 'UPCOM';
    case '30':
      return 'VN30';
    case 'SML':
      return 'VNSML';
    case 'MID':
      return 'VNMID';
    case 'ALL':
      return 'VNALL';
    case '100':
      return 'VN100';
    case 'HNXCON':
      return 'HNXCon';
    case 'X50':
      return 'VNX50';
    case 'SI':
      return 'VNSI';
    default:
      return code;
  }
};

export const renderValue = (value?: number | string, isPrice?: boolean) => {
  if (!value) {
    return '0';
  }
  if (typeof value === 'string') {
    return value;
  }
  const language = detectLanguage();
  const converted = isPrice
    ? value.toFixed()
    : value
      .toFixed(2)
      .replace('.00', '')
      .replace('.', language === 'vi' ? ',' : '.');
  return converted
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, language === 'vi' ? '.' : ',');
};

export const catchErrorDestroyAPI = (error: any) => {
  if (axios.isCancel(error)) {
    // eslint-disable-next-line no-console
    console.log('Request canceled');
  } else {
    throw error;
  }
};

export const checkZeroNumberRender = (data: string) => {
  if (!Number(data)) return '-';
  return data;
};

export const checkPositiveRender = (data?: number) => {
  if (data && data >= 0) return '+';
  return '';
};

export const parseStringToNumber = (value: any) => (value ? Number(value.toString().replaceAll(',', '')) : 0);

export const parseStringToNumberOrUndefined = (value?: string) => (value
  ? Number(value) : undefined);

export const getTimeTodayWithTime = (time: string) => {
  const splitTime = time.split(':');
  const today = new Date();
  today.setHours(Number(splitTime[0]));
  today.setMinutes(Number(splitTime[1]));
  today.setSeconds(Number(splitTime[2]));
  return today.getTime();
};

export const getHourMinuteFromTime = (time: string) => {
  const splitTime = time.split(':');
  return `${splitTime[0]}:${splitTime[1]}`;
};

export const ratingCount = (numberRated?: number) => {
  if (!numberRated) return 0;
  if (numberRated < 1000) return numberRated;
  return `${Math.floor(numberRated / 100) / 10.0} K`;
};

export function replaceNull(someObj: Object, replaceValue = '') {
  const replacer = (key: any, value: any) => (String(value) === 'null' || String(value) === 'undefined' ? replaceValue : value);
  // ^ because you seem to want to replace (strings) "null" or "undefined" too

  return JSON.parse(JSON.stringify(someObj, replacer));
}

export function replaceNullString(value?: string) {
  if (value) return value;
  return '';
}

export const formatDateYYYYMMDD = (date?: string) => {
  if (!date) return '';
  const dateFormat = new Date(date);
  let day: string | number = dateFormat.getDate();
  let month: string | number = dateFormat.getMonth() + 1;
  if (day < 10) {
    day = `0${day}`;
  }
  if (month < 10) {
    month = `0${month}`;
  }
  return `${dateFormat.getFullYear()}-${month}-${day}`;
};

export function convertSplitedDate(date?: string) {
  if (date) {
    const splitDate = date.split('-');
    return new Date(Number(splitDate[0]), Number(splitDate[1]) - 1, Number(splitDate[2]));
  }
  return undefined;
}
export const nextThreeDaysWithCurrent = () => {
  const current = new Date();
  const result = new Date();
  if (current.getDay() === 4 || current.getDay() === 5) {
    result.setDate(current.getDate() + 4);
  } else { result.setDate(current.getDate() + 2); }
  return result;
};

/**
   * 0 -> "3 Tháng"
   * 1 -> "6 Tháng"
   * 2 -> "12 "Tháng"
   * 3 -> "Đến đáo hạn"
   * 4 -> "9 tháng"
   */
export const ConvertDayNum = (value: number): string => {
  if (value === 0) {
    return 'register.maturity_time';
  }
  return `${value} ${value > 1 ? 'register.months' : 'register.month'}`;
};

export const convertProductPeriod = (value: number): string => {
  switch (value) {
    case 0:
      return '3 Tháng';
    case 1:
      return '6 Tháng';
    case 2:
      return '12 Tháng';
    case 3:
      return 'Đến đáo hạn';
    case 4:
      return '9 tháng';

    default:
      return '';
  }
};
/**
   * 1 -> "Sơ cấp"
   * 2 -> "Thứ cấp"
   */
export const convertProductCategory = (category?: number, type?: number): string => {
  switch (category) {
    case 1:
      return 'individual_customers.primary';
    case 2:
      return type === 0 ? 'individual_customers.flexible' : 'individual_customers.period';
    default:
      return '';
  }
};

/**
   * 0 -> "Bán"
   * 1 -> "Mua"
   */
export const convertProductMethod = (method?: string): string => {
  switch (method) {
    case '0':
      return 'register.sell';
    case '1':
      return 'register.buy';
    default:
      return '';
  }
};

export const durationDays = (date1?: string, date2?: string) => {
  if (!date1 || !date2) {
    return 1;
  }
  const day1 = new Date(date1);
  const day2 = new Date(date2);

  const difference = Math.abs(day2.getTime() - day1.getTime());

  return difference / (1000 * 3600 * 24);
};

export const getDateWithAddYear = (dt: Date, y: number) => {
  if (y <= 0) return dt;
  return (new Date(dt.setFullYear(dt.getFullYear() + y)));
};

const convertDate = (date: string) => {
  const splitDate = date.split('-');
  return new Date(
    Number(splitDate[0]),
    Number(splitDate[1]) - 1,
    Number(splitDate[2]),
  );
};

const getAge = (dateString: string, issueDate: string) => {
  const today = convertDate(issueDate);
  const birthDate = convertDate(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const convertExpiredDate = (
  cardType: string,
  issueDate?: string,
  birthDate?: string,
  issueExpired?: string,
): Date => {
  if (!issueDate || !birthDate) return convertDate('2099-12-31');
  switch (cardType) {
    case 'cmnd_09':
    case 'cmnd_12': {
      const validExpiredDate = getDateWithAddYear(convertDate(issueDate), 15);
      return validExpiredDate;
    }
    case 'cccd_12':
    case 'cccd_chip': {
      if (!issueExpired) return convertDate('2099-12-31');
      const old = getAge(birthDate, issueDate);
      if (old < 23) return getDateWithAddYear(convertDate(birthDate), 25);
      if (old >= 23 && old <= 37) return getDateWithAddYear(convertDate(birthDate), 40);
      if (old >= 38 && old <= 57) return getDateWithAddYear(convertDate(birthDate), 60);
      return convertDate('2099-12-31');
    }
    default:
      return convertDate('2099-12-31');
  }
};

export const capitalize = (text: string | undefined) => {
  if (!text) return '';
  return text.toLowerCase().replace(/(?:^|\s|[-"'([{])+\S/g, (c) => c.toUpperCase());
};

export const removeAccents = (text?: string) => {
  if (!text) return '';
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const validDimensionByMimetype = (mimeType: string) => {
  let width = 0;
  let height = 0;
  if (mimeType === 'image/png') {
    width = 1280;
    height = 960;
  } else {
    width = 1920;
    height = 1440;
  }
  return {
    width,
    height,
  };
};

export const compressImage = (
  file: File,
  mineType: string,
  isSignature: boolean = false,
): Promise<{
  returnedFile: File,
  dataUrl: string,
}> => {
  let resolver = (data: any) => data;
  const url = URL.createObjectURL(file);
  const imager = document.createElement('img');
  imager.src = url;
  const canvas = document.createElement('canvas');
  const validDimension = validDimensionByMimetype(mineType);
  imager.onload = () => {
    const maxWidth = isSignature ? 300 : validDimension.width;
    const maxHeight = isSignature ? 300 : validDimension.height;

    const imgwidth = imager.width;
    const imgheight = imager.height;
    let ratio = Math.min(maxWidth / imgwidth, maxHeight / imgheight);
    if (ratio > 1) {
      ratio = 1;
    }

    const newWidth = imgwidth * ratio;
    const newHeight = imgheight * ratio;
    if ((newWidth < 480 || newHeight < 480) && !isSignature) {
      if (newWidth < newHeight) {
        canvas.width = 960;
        canvas.height = (newHeight * 960) / newWidth;
      } else {
        canvas.height = 960;
        canvas.width = (newWidth * 960) / newHeight;
      }
    } else {
      canvas.width = newWidth;
      canvas.height = newHeight;
    }
    canvas.getContext('2d')?.drawImage(
      imager,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    canvas.toBlob(
      (b) => {
        if (b) {
          const result = {
            returnedFile: new File([b], file?.name || 'filename', { type: mineType }),
            dataUrl: URL.createObjectURL(b),
          };
          resolver(result);
        }
      },
      mineType,
      isSignature ? 0.6 : 1,
    );
  };
  return new Promise((resolve) => {
    resolver = resolve;
  });
};

export const reduceLocationList = (arr: OptionType[]) => {
  const ids = arr.map((o) => o.id);
  const filtered = arr.filter((ele, index) => !ids.includes(ele.id, index + 1));
  return filtered;
};

export const downloadFile = (path: string, name?: string) => {
  const a = document.createElement('a');
  a.target = '_blank';
  a.download = name || `${Date.now()}`;
  a.href = path;
  a.click();
  URL.revokeObjectURL(path);
  a.remove();
};

export const toBase64 = (file: File) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

export const mappingURLToExternal = (path?: string) => (path ? new URL(path, window.location.origin).href : '');
